/**
 * Prompt Service - API methods for prompt-related operations
 * Handles CRUD operations, social interactions, and prompt management
 * Provides optimistic updates and proper error handling
 */

import { apiService, type PaginatedResponse } from './apiService';
import { authService } from './authService';

// Types
export interface PromptCategory {
  id: number;
  type: string;
  name: string;
  description?: string;
}

export interface Prompt {
  public_id: string;
  slug: string | null;       // SEO-friendly slug e.g. "kanjeevaram-bridal-jewellery-5368b382"
  title?: string | null;
  description?: string | null;
  text: string;
  preview_url?: string;
  preview_image?: string;
  is_featured: boolean;
  view_count: number;
  created_at: string;
  updated_at: string;
  user: {
    public_id: string;
    name: string;
    verified: boolean;
  };
  categories: PromptCategory[];
  tags: {
    id: number;
    name: string;
    slug: string;
  }[];

  likes_count: number;
  comments_count: number;
  saves_count: number;
}

export interface CreatePromptData {
  text: string;
  title?: string;
  description?: string;
  categories: number[];
  tags?: string[];
  preview_url?: string;
  preview_image?: File;
}

export interface PromptFilters {
  category_ids?: number[];
  category_type?: string;
  user_public_id?: string;
  search?: string;
  tag_ids?: number[];
  tag_names?: string[];
  page?: number;
  per_page?: number;
}

export interface Comment {
  id: number;
  text: string;
  user_id: number;
  prompt_id: number;
  is_edited: boolean;
  created_at: string;
  user: {
    id: number;
    name: string;
    verified: boolean;
  };
}

export interface InteractionStatus {
  is_liked: boolean;
  is_saved: boolean;
  has_commented: boolean;
  likes_count: number;
  comments_count: number;
  saves_count: number;
}

class PromptService {
  private appendSearchParam(params: URLSearchParams, key: string, value: unknown) {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => params.append(`${key}[]`, String(item)));
      return;
    }

    params.append(key, String(value));
  }

  /**
   * Get paginated list of prompts
   */
  async getPrompts(filters: PromptFilters = {}): Promise<PaginatedResponse<Prompt>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => this.appendSearchParam(params, key, value));

    const response = await apiService.get<PaginatedResponse<Prompt>>(
      `/prompts?${params.toString()}`
    );

    return response.data!;
  }

  /**
   * Get single prompt by slug (new SEO route)
   * Falls back to getPrompt(publicId) if slug is null
   */
  async getPromptBySlug(slug: string): Promise<{ prompt: Prompt; related: Prompt[] }> {
    const response = await apiService.get<{ prompt: Prompt; related: Prompt[] }>(
      `/p/${slug}`
    );
    const result = response.data!;
    if (!result.prompt.categories) result.prompt.categories = [];
    return result;
  }

  /**
   * Build the canonical URL path for a prompt.
   * Prefer slug if available, fall back to public_id (legacy).
   */
  promptUrl(prompt: Pick<Prompt, 'public_id' | 'slug'>): string {
    return prompt.slug ? `/p/${prompt.slug}` : `/p/${prompt.public_id}`;
  }

  /**
   * Get single prompt by ID
   */
  async getPrompt(publicId: string): Promise<{ prompt: Prompt; related: Prompt[] }> {
    const response = await apiService.get<{ prompt: Prompt; related: Prompt[] }>(
      `/prompts/${publicId}`
    );

    const result = response.data!;

    // preview_image is now stored as filename only in database
    // Frontend will construct full URLs using imagePathHelper
    if (!result.prompt.categories) {
      result.prompt.categories = [];
    }

    return result;
  }

  /**
   * Create new prompt
   */
  async createPrompt(data: CreatePromptData): Promise<{ prompt: Prompt }> {
    const currentUser = authService.getCurrentUser();
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'moderator';

    if (data.preview_image) {
      const formData = new FormData();
      formData.append('text', data.text);
      if (data.title) formData.append('title', data.title);
      if (data.description) formData.append('description', data.description);
      data.categories.forEach((categoryId) => {
        formData.append('categories[]', categoryId.toString());
      });
      formData.append('preview_image', data.preview_image);

      if (data.tags?.length) {
        data.tags.forEach((tag) => formData.append('tags[]', tag));
      }

      if (data.preview_url) {
        formData.append('preview_url', data.preview_url);
      }

      if (isAdmin) {
        formData.append('is_admin', '1');
      }

      const response = await apiService.post<{ prompt: Prompt }>('/prompts', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data!;
    }

    const response = await apiService.post<{ prompt: Prompt }>('/prompts', {
      text: data.text,
      title: data.title,
      description: data.description,
      categories: data.categories,
      tags: data.tags,
      preview_url: data.preview_url,
      ...(isAdmin ? { is_admin: true } : {}),
    });

    return response.data!;
  }

  /**
   * Update existing prompt
   */
  async updatePrompt(publicId: string, data: Partial<CreatePromptData>): Promise<{ prompt: Prompt }> {
    const payload: Record<string, unknown> = {};

    if (data.text !== undefined) payload.text = data.text;
    if (data.preview_url !== undefined) payload.preview_url = data.preview_url;
    if (data.categories !== undefined) payload.categories = data.categories;
    if (data.preview_image !== undefined) payload.preview_image = data.preview_image;

    const currentUser = authService.getCurrentUser();
    if (currentUser?.role === 'admin' || currentUser?.role === 'moderator') {
      payload.is_admin = true;
    }

    const response = await apiService.patch<{ prompt: Prompt }>(`/prompts/${publicId}`, payload);
    return response.data!;
  }

  /**
   * Delete prompt
   */
  async deletePrompt(publicId: string): Promise<void> {
    await apiService.delete(`/prompts/${publicId}`);
  }

  /**
   * Get popular prompts
   */
  async getPopularPrompts(days: number = 7, limit: number = 10): Promise<{ prompts: Prompt[] }> {
    const response = await apiService.get<{ prompts: Prompt[] }>(
      `/prompts/popular?days=${days}&limit=${limit}`
    );

    return response.data!;
  }

  /**
   * Get trending prompts
   */
  async getTrendingPrompts(hours: number = 24, limit: number = 10): Promise<{ prompts: Prompt[] }> {
    const response = await apiService.get<{ prompts: Prompt[] }>(
      `/prompts/trending?hours=${hours}&limit=${limit}`,
      {
        skipRetry: true,
      }
    );

    return response.data!;
  }

  /**
   * Search prompts
   * API returns { status, message, data: { prompts, pagination } }
   */
  async searchPrompts(
    query: string,
    filters: Omit<PromptFilters, 'search'> = {}
  ): Promise<{ data: Prompt[]; pagination: PaginatedResponse<Prompt>['pagination'] | null }> {
    const params = new URLSearchParams({ q: query });
    Object.entries(filters).forEach(([key, value]) => this.appendSearchParam(params, key, value));

    const response = await apiService.get<{ prompts: Prompt[]; pagination: PaginatedResponse<Prompt>['pagination'] }>(
      `/prompts/search?${params.toString()}`
    );

    const payload = (response as any)?.data ?? {};
    return {
      data: payload.prompts ?? [],
      pagination: payload.pagination ?? null,
    };
  }

  /**
   * Get prompts by category
   */
  async getPromptsByCategory(
    categoryType: string,
    categoryId: number,
    page: number = 1,
    perPage: number = 20
  ): Promise<PaginatedResponse<Prompt>> {
    return this.getPrompts({
      page,
      per_page: perPage,
      category_type: categoryType,
      category_ids: [categoryId],
    });
  }

  /**
   * Get user's prompts
   */
  async getUserPrompts(userPublicId: string, page: number = 1, perPage: number = 20): Promise<PaginatedResponse<Prompt>> {
    const response = await apiService.get<PaginatedResponse<Prompt>>(
      `/prompts/user/${userPublicId}?page=${page}&per_page=${perPage}`
    );

    return response.data!;
  }

  /**
   * Toggle like on prompt
   */
  async toggleLike(promptId: string): Promise<{
    action: 'liked' | 'unliked';
    likes_count: number;
    is_liked: boolean;
  }> {
    const response = await apiService.post<{
      action: 'liked' | 'unliked';
      likes_count: number;
      is_liked: boolean;
    }>(`/prompts/${promptId}/like`);

    return response.data!;
  }

  /**
   * Toggle save on prompt
   */
  async toggleSave(promptId: string): Promise<{
    action: 'saved' | 'unsaved';
    saves_count: number;
    is_saved: boolean;
  }> {
    const response = await apiService.post<{
      action: 'saved' | 'unsaved';
      saves_count: number;
      is_saved: boolean;
    }>(`/prompts/${promptId}/save`);

    return response.data!;
  }

  /**
   * Get interaction status for prompt
   */
  async getInteractionStatus(promptId: string): Promise<InteractionStatus> {
    const response = await apiService.get<InteractionStatus>(
      `/prompts/${promptId}/interaction-status`
    );

    return response.data!;
  }

  /**
   * Get comments for prompt
   */
  async getComments(promptId: string, page: number = 1, perPage: number = 50): Promise<PaginatedResponse<Comment>> {
    const response = await apiService.get<PaginatedResponse<Comment>>(
      `/prompts/${promptId}/comments?page=${page}&per_page=${perPage}`
    );

    return response.data!;
  }

  /**
   * Create comment on prompt
   * @param promptId - The prompt public ID
   * @param text - Comment text content
   * @param parentId - Optional parent comment ID for replies
   */
  async createComment(promptId: string, text: string, parentId?: number): Promise<{ comment: Comment }> {
    const payload: { text: string; parent_id?: number } = { text };
    if (parentId) {
      payload.parent_id = parentId;
    }

    const response = await apiService.post<{ comment: Comment }>(
      `/prompts/${promptId}/comments`,
      payload
    );

    return response.data!;
  }

  /**
   * Delete comment
   */
  async deleteComment(commentId: number): Promise<void> {
    await apiService.delete(`/comments/${commentId}`);
  }

  /**
   * Get user's saved prompts
   */
  async getSavedPrompts(page: number = 1, perPage: number = 30): Promise<PaginatedResponse<Prompt>> {
    const response = await apiService.get<PaginatedResponse<Prompt>>(
      `/user/saves?page=${page}&per_page=${perPage}`
    );

    return response.data!;
  }

  /**
   * Get user's recent comments
   */
  async getRecentComments(limit: number = 10): Promise<{ comments: Comment[] }> {
    const response = await apiService.get<{ comments: Comment[] }>(
      `/user/comments/recent?limit=${limit}`
    );

    return response.data!;
  }

  /**
   * Get user's most liked prompts
   */
  async getMostLikedPrompts(limit: number = 10): Promise<{ prompts: Prompt[] }> {
    const response = await apiService.get<{ prompts: Prompt[] }>(
      `/user/prompts/most-liked?limit=${limit}`
    );

    return response.data!;
  }

  /**
   * Get prompt statistics
   */
  async getPromptStats(): Promise<{
    total_prompts: number;
    featured_prompts: number;
    total_views: number;
    active_users: number;
    prompts_today: number;
    prompts_this_week: number;
    average_likes_per_prompt: number;
  }> {
    const response = await apiService.get<{
      stats: {
        total_prompts: number;
        featured_prompts: number;
        total_views: number;
        active_users: number;
        prompts_today: number;
        prompts_this_week: number;
        average_likes_per_prompt: number;
      };
    }>('/prompts/stats');

    return response.data!.stats;
  }

  /**
   * Report prompt
   */
  async reportPrompt(promptId: string, reason: string, details?: string): Promise<{ report: any }> {
    const response = await apiService.post<{ report: any }>(
      `/prompts/${promptId}/report`,
      { reason, details }
    );

    return response.data!;
  }

  /**
   * Get available report reasons
   */
  async getReportReasons(): Promise<{ reasons: Record<string, string> }> {
    const response = await apiService.get<{ reasons: Record<string, string> }>('/reports/reasons');
    return response.data!;
  }
}

// Export singleton instance
export const promptService = new PromptService();
export default promptService;